import { useQuery } from "@tanstack/react-query";
import schedulerApi from "../../../services/api/SchedulerApiService";
import { useAuthStore } from "../../zustand";
import { getRandomVideoForPhysio } from "../../../util/videoHelpers";

/**
 * Custom hook for fetching and caching home dashboard data
 *
 * Provides calendar events, notification list, and schedule data for the home/dashboard view.
 * Uses TanStack Query as the single source of truth - no redundant Zustand caching.
 * Data is cached per language to enable instant language switching without API refetch.
 *
 * Calendar events are derived from notifications with video title lookups for physio users.
 *
 * @param {Object} videosData - Video library data for physio user title lookups
 * @param {Array} videosData.allVideos - Complete video library
 * @param {Array} videosData.physioVideos - Physio-specific videos
 * @returns {Object} Query result object
 */
export const useHomeDataQuery = (videosData = null) => {
  const email = useAuthStore((state) => state.user.email);
  const preferredLanguage = useAuthStore((state) => state.preferredLanguage);

  const getTimezoneOffset = () => {
    const offset = new Date().getTimezoneOffset() / -60;
    return offset >= 0
      ? `+${String(Math.abs(offset)).padStart(2, "0")}`
      : `-${String(Math.abs(offset)).padStart(2, "0")}`;
  };

  const timezoneOffset = getTimezoneOffset();

  const query = useQuery({
    queryKey: ["homeData", email, timezoneOffset, preferredLanguage],

    queryFn: async () => {
      const response = await schedulerApi.getHomeData(
        timezoneOffset,
        preferredLanguage,
      );

      // Derive calendar events with video title lookups
      const calendarEvents = deriveCalendarEvents(
        response.notifications || [],
        videosData || { allVideos: [], physioVideos: [] },
      );

      const allSchedules =
        response.notifications?.flatMap(
          (notification) => notification.schedules || [],
        ) || [];

      return {
        notifications: response.notifications || [],
        schedules: allSchedules,
        calendarEvents: calendarEvents,
        rawHomeData: response,
      };
    },

    enabled: !!email,

    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    placeholderData: (previousData) => previousData,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    structuralSharing: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Derive calendar events from notifications array with video title lookups
 * Handles physio user events, temp events, and regular user events
 */
function deriveCalendarEvents(notifications, videosData) {
  if (!notifications || !Array.isArray(notifications)) {
    return [];
  }

  const calendarEvents = [];

  // Combine videos for physio user event lookups
  const combinedVideos = [
    ...(videosData?.allVideos || []),
    ...(videosData?.physioVideos || []),
  ];

  notifications.forEach((notification) => {
    const schedules = notification.schedules || [];

    schedules.forEach((schedule) => {
      // Skip PHYSIO schedules without video title
      if (schedule.type === "PHYSIO" && !schedule.video?.title) {
        return;
      }

      // Determine color based on schedule type
      let color;
      switch (schedule.type) {
        case "SYSTEM":
          color = "black";
          break;
        case "USER":
          color = "#073bd0";
          break;
        case "PHYSIO":
          color = "#05b67f";
          break;
        default:
          color = "#063bd0";
      }

      // Determine title
      let title;
      if (schedule.video?.title) {
        // PHYSIO events with embedded video - use video title
        title = schedule.video.title;
      } else if (schedule.preferenceId === 2 && combinedVideos.length > 0) {
        // Physio user-created events (preferenceId=2, no video object)
        // Check if this is a temp ID (optimistic update in progress)
        const isTempId =
          typeof schedule.id === "string" && schedule.id.startsWith("temp-");

        if (isTempId) {
          // Use the schedule name (placeholder) for temp events
          title = schedule.name;
        } else {
          // Use schedule.id as seed for real events
          const matchingVideo = getRandomVideoForPhysio(
            combinedVideos,
            schedule.id,
          );
          title = matchingVideo?.title || schedule.name;
        }
      } else {
        // Regular events - use schedule name
        title = schedule.name;
      }

      // Create calendar event object
      calendarEvents.push({
        title: title,
        date: schedule.notificationTime,
        color: color,
        meridiem: "short",
        id: schedule.id,
      });
    });
  });

  return calendarEvents;
}
